import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { MOVE_PROJECT_MODAL } from '@chegg-wt/core/state/modals/constants'
import { moveProjects } from '@chegg-wt/core/state/folders/actions'
import {
  moveProjectModalTitleSelector,
  moveProjectModalDescriptionSelector,
  idsFromModalDataSelector as projectIdsFromModalDataSelector,
  modalDataSelector,
} from '@chegg-wt/core/state/modals/selectors'
import { foldersForSelectBoxSelector } from '@chegg-wt/core/state/folders/selectors'
import { TestingTargetProp } from '@chegg-wt/core/types/react'
import { AppState } from '@chegg-wt/core/state'
import Select from '@chegg-wt/core/components/Select'
import { publicAssetUrl } from '@chegg-wt/core/utils/url'
import { Option } from '@chegg-wt/core/types/core'
import { closeModal } from '@chegg-wt/core/state/modals/actions'
import * as Styled from '../styled'
import { BaseModalProps } from '../BaseModal'
import { Modal } from '..'
import * as FStyled from './styled'
import {
  MOVE_MODAL_TITLE,
  MOVE_MODAL_DESCRIPTION,
  MOVE_MODAL_CANCEL,
  MOVE_MODAL_CONFIRM,
} from './testIds'

export interface MoveProjectModalProps extends BaseModalProps, TestingTargetProp {
  title: string
  description: string
  selectBoxItems: Option[]
  projectIds: string[]
  handleConfirm: (folderId: number, projectIds: string[]) => void
}

export const MoveProjectModal: React.FunctionComponent<MoveProjectModalProps> = ({
  title,
  description,
  selectBoxItems,
  projectIds,
  handleClose,
  handleConfirm,
}) => {
  const [folderId, setFolderId] = useState(null)

  const onConfirmClick = useCallback(() => {
    handleConfirm(folderId, projectIds)
  }, [handleConfirm, folderId, projectIds])

  const onClickOption = (id: string) => setFolderId(parseInt(id, 10))

  return (
    <Modal showModal closeButton outsideClick>
      <Styled.Container>
        <FStyled.Heading data-test={MOVE_MODAL_TITLE}>{title}</FStyled.Heading>
        <Styled.Message data-test={MOVE_MODAL_DESCRIPTION}>{description}</Styled.Message>
        <Select
          onSelect={onClickOption}
          options={selectBoxItems}
          trigger={({ openDropdown, selected }) => (
            <FStyled.Layout onClick={openDropdown}>
              <FStyled.Face data-test="move-project-dropdown">
                <FStyled.Label>{selected.name || 'Choose a folder'}</FStyled.Label>
                <FStyled.Icon
                  icon={publicAssetUrl('up-down-arrows.svg')}
                  iconWidth="9px"
                  iconHeight="18px"
                />
              </FStyled.Face>
            </FStyled.Layout>
          )}
          type="folders"
        />
        <Styled.ButtonContainer>
          <FStyled.CancelButton
            type="button"
            onClick={handleClose}
            data-test={MOVE_MODAL_CANCEL}
          >
            Cancel
          </FStyled.CancelButton>
          <FStyled.ConfirmButton
            disabled={!folderId}
            onClick={onConfirmClick}
            data-test={MOVE_MODAL_CONFIRM}
          >
            Move
          </FStyled.ConfirmButton>
        </Styled.ButtonContainer>
        <Styled.ButtonContainerMobile>
          <FStyled.ConfirmButton
            disabled={!folderId}
            onClick={onConfirmClick}
            data-test={MOVE_MODAL_CONFIRM}
          >
            Move
          </FStyled.ConfirmButton>
          <FStyled.CancelButton
            type="button"
            onClick={handleClose}
            data-test={MOVE_MODAL_CANCEL}
          >
            Cancel
          </FStyled.CancelButton>
        </Styled.ButtonContainerMobile>
      </Styled.Container>
    </Modal>
  )
}

const mapStateToProps = (state: AppState) => ({
  // TODO: stop using the second param here, prefer pattern seen in WFE
  title: moveProjectModalTitleSelector(state, modalDataSelector(state)),
  description: moveProjectModalDescriptionSelector(state, modalDataSelector(state)),
  selectBoxItems: foldersForSelectBoxSelector(state),
  projectIds: projectIdsFromModalDataSelector(state),
})

const mapDispatchToProps = {
  handleConfirm: (folderId, projectIds) => moveProjects({ folderId, projectIds }),
  handleClose: () => closeModal(MOVE_PROJECT_MODAL),
}

export default connect(mapStateToProps, mapDispatchToProps)(MoveProjectModal)
